import { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLineItemCategories } from '@/hooks/useConfigurableLists';
import { useSubItemCategories } from '@/hooks/useSubItemCategories';
import {
  useCategorySubItemDefaults,
  useAddCategorySubItemDefault,
  useUpdateCategorySubItemDefault,
  useDeleteCategorySubItemDefault,
} from '@/hooks/useCategorySubItemDefaults';
import type { CategorySubItemDefault, SubItemCategory } from '@jobos/shared';

// --- Defaults editor for a single line item category ---

function CategoryDefaultsList({
  lineItemCategory,
  subItemCategories,
}: {
  lineItemCategory: string;
  subItemCategories: SubItemCategory[];
}) {
  const { data: allDefaults = [] } = useCategorySubItemDefaults();
  const addMutation = useAddCategorySubItemDefault();
  const updateMutation = useUpdateCategorySubItemDefault();
  const deleteMutation = useDeleteCategorySubItemDefault();

  const defaults = useMemo(
    () => allDefaults.filter((d) => d.lineItemCategory === lineItemCategory),
    [allDefaults, lineItemCategory]
  );

  const [newSubItemKey, setNewSubItemKey] = useState('');
  const [newHours, setNewHours] = useState('');
  const [newRate, setNewRate] = useState('');
  const [newRateType, setNewRateType] = useState<'hourly' | 'daily'>('hourly');
  const [newAmount, setNewAmount] = useState('');
  const [newNote, setNewNote] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSubItemKey, setEditSubItemKey] = useState('');
  const [editHours, setEditHours] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editRateType, setEditRateType] = useState<'hourly' | 'daily'>('hourly');
  const [editAmount, setEditAmount] = useState('');
  const [editNote, setEditNote] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const getCategory = (key: string) => subItemCategories.find((c) => c.key === key);
  const isQtyRate = (key: string) => getCategory(key)?.pricingMode === 'qty_rate';
  const getCategoryLabel = (key: string) => getCategory(key)?.label ?? key;

  const selectedNewIsQtyRate = isQtyRate(newSubItemKey);

  const handleAdd = () => {
    if (!newSubItemKey) return;
    addMutation.mutate(
      {
        lineItemCategory,
        subItemCategoryKey: newSubItemKey,
        ...(selectedNewIsQtyRate
          ? {
              defaultHours: newHours ? parseFloat(newHours) : undefined,
              defaultRate: newRate ? parseFloat(newRate) : undefined,
              defaultRateType: newRateType,
            }
          : {
              defaultAmount: newAmount ? parseFloat(newAmount) : undefined,
            }),
        defaultNote: newNote || undefined,
        sortOrder: defaults.length,
      },
      {
        onSuccess: () => {
          setNewSubItemKey('');
          setNewHours('');
          setNewRate('');
          setNewRateType('hourly');
          setNewAmount('');
          setNewNote('');
        },
      }
    );
  };

  const handleStartEdit = (item: CategorySubItemDefault) => {
    setEditingId(item.id);
    setEditSubItemKey(item.subItemCategoryKey);
    setEditHours(item.defaultHours != null ? String(item.defaultHours) : '');
    setEditRate(item.defaultRate != null ? String(item.defaultRate) : '');
    setEditRateType(item.defaultRateType ?? 'hourly');
    setEditAmount(item.defaultAmount != null ? String(item.defaultAmount) : '');
    setEditNote(item.defaultNote ?? '');
  };

  const handleSaveEdit = () => {
    if (!editingId || !editSubItemKey) return;
    const editIsQtyRate = isQtyRate(editSubItemKey);
    updateMutation.mutate({
      id: editingId,
      subItemCategoryKey: editSubItemKey,
      ...(editIsQtyRate
        ? {
            defaultHours: editHours ? parseFloat(editHours) : null,
            defaultRate: editRate ? parseFloat(editRate) : null,
            defaultRateType: editRateType,
            defaultAmount: null,
          }
        : {
            defaultAmount: editAmount ? parseFloat(editAmount) : null,
            defaultHours: null,
            defaultRate: null,
            defaultRateType: null,
          }),
      defaultNote: editNote || null,
    });
    setEditingId(null);
  };

  const handleCancelEdit = () => setEditingId(null);

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
    setDeletingId(null);
  };

  // Filter out sub-item categories already added (except when editing)
  const availableCategories = subItemCategories.filter(
    (c) => !defaults.some((d) => d.subItemCategoryKey === c.key && d.id !== editingId)
  );

  return (
    <div className="pl-8 pr-4 pb-3 pt-1 bg-gray-50/50">
      {defaults.map((item) => (
        <div key={item.id} className="flex items-center justify-between py-1.5 group">
          {editingId === item.id ? (
            <div className="flex items-center gap-2 flex-1 flex-wrap">
              <select
                value={editSubItemKey}
                onChange={(e) => setEditSubItemKey(e.target.value)}
                className="h-7 text-sm border rounded px-2 bg-white"
              >
                <option value="">Select...</option>
                {subItemCategories
                  .filter((c) => c.key === editSubItemKey || !defaults.some((d) => d.subItemCategoryKey === c.key && d.id !== editingId))
                  .map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
              </select>
              {isQtyRate(editSubItemKey) ? (
                <>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="Hours"
                    value={editHours}
                    onChange={(e) => setEditHours(e.target.value)}
                    className="w-20 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Rate"
                      value={editRate}
                      onChange={(e) => setEditRate(e.target.value)}
                      className="w-24 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <div className="inline-flex rounded-md border border-gray-300 text-xs overflow-hidden">
                    <button
                      type="button"
                      className={`px-2 py-0.5 ${editRateType === 'hourly' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setEditRateType('hourly')}
                    >
                      /hr
                    </button>
                    <button
                      type="button"
                      className={`px-2 py-0.5 ${editRateType === 'daily' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setEditRateType('daily')}
                    >
                      /day
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Amount"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    className="w-24 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              )}
              <Input
                placeholder="Note"
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                className="w-32 h-7 text-sm"
              />
              <Button size="sm" variant="ghost" onClick={handleSaveEdit} className="h-6 w-6 p-0">
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelEdit} className="h-6 w-6 p-0">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : deletingId === item.id ? (
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xs text-red-600">Delete "{getCategoryLabel(item.subItemCategoryKey)}" default?</span>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} className="h-6 text-xs">
                Confirm
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDeletingId(null)} className="h-6 text-xs">
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <span className="text-sm text-gray-700">
                {getCategoryLabel(item.subItemCategoryKey)}
                {isQtyRate(item.subItemCategoryKey) ? (
                  <span className="text-gray-500">
                    {item.defaultHours != null ? ` — ${item.defaultHours}h` : ''}
                    {item.defaultRate != null ? ` @ $${item.defaultRate.toFixed(2)}${item.defaultRateType === 'daily' ? '/day' : '/hr'}` : ''}
                  </span>
                ) : (
                  item.defaultAmount != null && (
                    <span className="text-gray-500"> — ${item.defaultAmount.toFixed(2)}</span>
                  )
                )}
                {item.defaultNote && (
                  <span className="text-gray-400 ml-2 text-xs">({item.defaultNote})</span>
                )}
              </span>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="ghost" onClick={() => handleStartEdit(item)} className="h-6 w-6 p-0">
                  <Pencil className="h-3 w-3 text-gray-500" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeletingId(item.id)} className="h-6 w-6 p-0">
                  <Trash2 className="h-3 w-3 text-gray-500" />
                </Button>
              </div>
            </>
          )}
        </div>
      ))}

      {/* Add default form */}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <select
          value={newSubItemKey}
          onChange={(e) => setNewSubItemKey(e.target.value)}
          className="h-7 text-sm border rounded px-2 bg-white"
        >
          <option value="">Sub-item category...</option>
          {availableCategories.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
        {newSubItemKey && selectedNewIsQtyRate ? (
          <>
            <Input
              type="number"
              min="0"
              step="0.5"
              placeholder="Hours"
              value={newHours}
              onChange={(e) => setNewHours(e.target.value)}
              className="w-20 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Rate"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                className="w-24 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="inline-flex rounded-md border border-gray-300 text-xs overflow-hidden">
              <button
                type="button"
                className={`px-2 py-0.5 ${newRateType === 'hourly' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                onClick={() => setNewRateType('hourly')}
              >
                /hr
              </button>
              <button
                type="button"
                className={`px-2 py-0.5 ${newRateType === 'daily' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                onClick={() => setNewRateType('daily')}
              >
                /day
              </button>
            </div>
          </>
        ) : newSubItemKey ? (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">$</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Amount"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              className="w-24 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        ) : null}
        {newSubItemKey && (
          <Input
            placeholder="Note (optional)"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            className="w-32 h-7 text-sm"
          />
        )}
        <Button
          onClick={handleAdd}
          disabled={!newSubItemKey || addMutation.isPending}
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
}

// --- Main page ---

export default function CategoryDefaultsSettings() {
  const { data: lineItemCategories = [], isLoading: categoriesLoading } = useLineItemCategories();
  const { data: subItemCats = [], isLoading: subItemLoading } = useSubItemCategories();
  const { data: allDefaults = [] } = useCategorySubItemDefaults();

  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const defaultCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of allDefaults) {
      counts[d.lineItemCategory] = (counts[d.lineItemCategory] || 0) + 1;
    }
    return counts;
  }, [allDefaults]);

  const toggleExpand = (category: string) => {
    setExpandedCategory((prev) => (prev === category ? null : category));
  };

  if (categoriesLoading || subItemLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Category Defaults</h2>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Category Defaults</h2>
      <p className="text-sm text-gray-500 mb-6">
        Configure default sub-items that auto-populate when a line item category is selected. Expand a category to manage its defaults.
      </p>

      <div className="border rounded-lg divide-y">
        {lineItemCategories.map((category) => {
          const isExpanded = expandedCategory === category;
          const count = defaultCounts[category] || 0;

          return (
            <div key={category}>
              <div
                className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer"
                onClick={() => toggleExpand(category)}
              >
                <div className="flex items-center gap-3">
                  <button type="button" className="p-0.5 -ml-1 rounded hover:bg-gray-200">
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-gray-500" />
                      : <ChevronRight className="h-4 w-4 text-gray-400" />
                    }
                  </button>
                  <span className="text-sm text-gray-900 font-medium">{category}</span>
                  {count > 0 && (
                    <span className="text-[10px] text-gray-400">
                      {count} default{count !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              {isExpanded && (
                <CategoryDefaultsList
                  lineItemCategory={category}
                  subItemCategories={subItemCats}
                />
              )}
            </div>
          );
        })}
        {lineItemCategories.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            No line item categories configured. Add some in Line Item Categories settings.
          </div>
        )}
      </div>
    </div>
  );
}
